/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * Reliable blob-to-file download utility.
 *
 * Some browsers (Edge, Chrome) ignore the `download` attribute on <a> elements
 * when the click occurs outside a user gesture context (e.g. after an async
 * fetch). This utility uses the File System Access API (showSaveFilePicker)
 * as the primary approach, which gives reliable filename control. It falls
 * back to the classic <a> click approach for unsupported browsers.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */

/**
 * Save a Blob (or raw parts) as a file download with a proper filename.
 *
 * @param data     A Blob, or array of BlobParts to wrap in a Blob
 * @param filename The desired filename (e.g. "QUO-202602-00007.pdf")
 * @param mimeType MIME type — always provide explicitly for best results
 */
export async function saveBlob(
  data: Blob | BlobPart[],
  filename: string,
  mimeType = 'application/octet-stream',
): Promise<void> {
  // Always create a fresh Blob with explicit type
  const blob = new Blob(
    data instanceof Blob ? [data] : data,
    { type: mimeType },
  );

  // Primary: File System Access API (Chrome 86+, Edge 86+)
  if (typeof (window as any).showSaveFilePicker === 'function') {
    try {
      const ext = filename.includes('.') ? '.' + filename.split('.').pop() : '';
      const handle = await (window as any).showSaveFilePicker({
        suggestedName: filename,
        types: ext
          ? [{ description: filename, accept: { [mimeType]: [ext] } }]
          : undefined,
      });
      const writable = await handle.createWritable();
      await writable.write(blob);
      await writable.close();
      return;
    } catch (err: any) {
      // User cancelled the Save dialog — do nothing
      if (err?.name === 'AbortError') return;
      // API failed for other reason — fall through to legacy
    }
  }

  // Fallback: classic <a> click with delayed cleanup
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();

  // Delay cleanup so the browser has time to initiate the download
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 40_000);
}
